import useGeneratedPayrollsService, {
  APPROVE_PAYROLL_REPORT,
  PAYROLL_REPORT_STATUSES,
} from 'domain/finance/payroll/generatedPayrolls/useGeneratedPayrollsService';
import ButtonWithConfirmationDateInput from 'domain/finance/shared/button/ButtonWithConfirmationDateInput';
import { TextField, useFieldHelper } from 'framework/ui/field';
import { useShowContext } from 'framework/show';
import { DataGridLayout, List, ListItem, useListContext } from 'framework/list';
import { Box } from '@mui/material';
import { InvoiceTotalField } from 'domain/shared/core/invoice';
import { EmployeeLinkField } from 'domain/shared/employee';
import { isEqual } from 'lodash';
import { useAsyncValue } from 'framework/utils';
import useAuthenticatedUserService from 'infrastructure/security/authentication/authenticatedUserService';
import TotalFieldDecorator from 'domain/finance/payroll/generatedPayrolls/shared/field/TotalFieldDecorator';

const ApprovePayrollReportButton = ({ statusSource }: { statusSource: string }) => {
  const { approvePayrollReport, getGrossSalary } = useGeneratedPayrollsService();
  const { getValue } = useFieldHelper({ source: statusSource });
  const { data } = useShowContext();
  const { updateInList = () => {}, data: listData } = useListContext();
  const status = getValue();
  const contextData = data.getData() || {};
  const hasApprovedReport = listData?.items?.some(
    (item: any) =>
      (item.status === PAYROLL_REPORT_STATUSES.APPROVED || item.status === PAYROLL_REPORT_STATUSES.PAID) &&
      item.period === contextData.period &&
      isEqual(item.offices, contextData.offices),
  );
  const { getUserAlias } = useAuthenticatedUserService();
  const userAlias = useAsyncValue(getUserAlias);

  const getDataMethod = async () => {
    const result = await getGrossSalary(contextData.id);
    const totalWorkingHours = result.reduce((acc: number, { workingHours }: any) => acc + workingHours, 0);
    const totalGrossSalary = result.reduce(
      (acc: Dictionary<number>, { currency, grossSalary }: any) => ({
        ...acc,
        [currency]: (acc[currency] || 0) + grossSalary,
      }),
      {},
    );

    if (result.length) {
      result.push({
        totalLabel: 'Total',
        totalWorkingHours,
        totalGrossSalary,
      });
    }

    return {
      result,
      totalElements: result.length,
      totalPages: 1,
    };
  };

  if (status !== PAYROLL_REPORT_STATUSES.PENDING || hasApprovedReport) {
    return null;
  }

  return (
    <ButtonWithConfirmationDateInput
      modalSize="sm"
      modalChildren={
        <Box
          sx={{
            background: 'var(--mui-palette-background-paper)',
            borderRadius: '4px',
            margin: '20px 0 8px',
            padding: '4px 12px 16px',
            position: 'relative',
            minHeight: '100px',
          }}
        >
          <List getDataMethod={getDataMethod}>
            <DataGridLayout
              showTotal={false}
              sxPaginationWrapper={{ display: 'none' }}
              sxTableWrapper={{
                '& table.MuiTable-root': {
                  minWidth: '520px',
                  '& th:first-child': {
                    width: 'auto',
                  },
                  '& tbody tr:last-child td': {
                    fontWeight: 600,
                  },
                },
              }}
            >
              <TotalFieldDecorator source="totalLabel" label="Worker">
                <EmployeeLinkField source="employeeAlias" nameSource="employeeName" />
              </TotalFieldDecorator>
              <TotalFieldDecorator source="totalWorkingHours" label="Working hours">
                <TextField source="workingHours" />
              </TotalFieldDecorator>
              <TotalFieldDecorator source="totalGrossSalary" label="Gross salary">
                <InvoiceTotalField source="grossSalary" currencySource="currency" />
              </TotalFieldDecorator>
            </DataGridLayout>
          </List>
        </Box>
      }
      displayMessage="Do you really want to approve this payroll report? Please, check data and fill out the input below."
      submitMethod={(date: string) => approvePayrollReport(contextData.id as string, date)}
      afterSubmit={{
        successMessage: 'The payroll report has been successfully approved.',
        errorMessage: "Can't approve this payroll report.",
        execute: () => {
          updateInList({ ...contextData, status: PAYROLL_REPORT_STATUSES.APPROVED, approvedBy: userAlias } as ListItem);
        },
      }}
      permissionToCheck={APPROVE_PAYROLL_REPORT}
      variant="outlined"
      inputLabel="Date"
    >
      Approve
    </ButtonWithConfirmationDateInput>
  );
};

export default ApprovePayrollReportButton;
